/*  ========================================================================
    # Sections - Testimonials
    ========================================================================  */

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSwipeable } from 'react-swipeable';
import useTranslation from '../../hooks/useTranslation';
import useSVGSprite from '../../hooks/useSVGSprite';
import tailwindConfig from '../../../../tailwind.config';
import LazyImage from '../../components/LazyImage';
import LazySource from '../../components/LazySource';

const Testimonials = ({ entries }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const __ = useTranslation();

  // Icons
  const sprite = useSVGSprite();

  // If there are fewer than 5 entries, the array is padded with existing ones.
  const sliderEntries = useMemo(() => {
    let tempEntries = [];

    if (entries.length > 1 && entries.length < 5) {
      tempEntries = [...entries];
      while (tempEntries.length < 5) tempEntries.push(...entries);
    } else {
      tempEntries = entries;
    }

    return tempEntries;
  }, [entries]);

  // Get the slide offset value.
  const getSlideOffset = index => {
    const delta = index - activeSlide;

    if (activeSlide === 0 && index === sliderEntries.length - 1) return -1;
    if (activeSlide === 0 && index === sliderEntries.length - 2) return -2;
    if (activeSlide === 1 && index === sliderEntries.length - 1) return -2;

    if (activeSlide === sliderEntries.length - 1 && index === 0) return 1;
    if (activeSlide === sliderEntries.length - 1 && index === 1) return 2;

    if (activeSlide === sliderEntries.length - 2 && index === 0) return 2;

    if (delta <= -3) return -2;
    if (delta >= 3) return 2;

    return delta;
  };

  // Navigate Left
  const navigateLeft = () => {
    if (activeSlide > 0) setActiveSlide(activeSlide - 1);
    else setActiveSlide(sliderEntries.length - 1);
  };

  // Navigate Right
  const navigateRight = () => {
    if (activeSlide < sliderEntries.length - 1) setActiveSlide(activeSlide + 1);
    else setActiveSlide(0);
  };

  // Add swipe handlers.
  const swipeHandlers = useSwipeable({
    onSwipedRight: navigateLeft,
    onSwipedLeft: navigateRight
  });

  return (
    <>
      <div className='s-testimonials__entries' {...swipeHandlers}>
        {sliderEntries.map((entry, index) => (
          <div
            key={`${entry.name}-${index}`}
            className='s-testimonials__entry'
            style={sliderEntries.length === 1 ? { opacity: 1 } : {}}
            data-offset={sliderEntries.length === 1 ? '' : getSlideOffset(index)}
          >
            <div className='s-testimonials__entry-text' dangerouslySetInnerHTML={{ __html: entry.text }}></div>
            <div className='s-testimonials__entry-person'>
              {entry.person.image ? (
                <LazyImage
                  figureClass='s-testimonials__entry-person-image'
                  defaultUrl={entry.person.image.sizes.thumbnail}
                  placeholderUrl={entry.person.image.sizes.placeholder}
                  alt={entry.person.image.alt}
                >
                  <LazySource
                    srcSet={entry.person.image.sizes.thumbnail}
                    mediaMin={tailwindConfig.theme.screens.sm}
                  />
                </LazyImage>
              ) : null}
              <div className={`s-testimonials__entry-person-description ${entry.person.image ? '' : 's-testimonials__entry-person-description--centered'}`}>
                <strong>{entry.person.name}</strong>
                {entry.person.company ? (
                  <span dangerouslySetInnerHTML={{ __html: entry.person.company }} />
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
      {entries.length > 1 && (
        <div className='s-testimonials__navigation'>
          <button
            className='s-testimonials__navigate-left'
            title={__('testimonial-navigate-left')}
            aria-label={__('testimonial-navigate-left')}
            onClick={navigateLeft}
          >
            <svg viewBox='0 0 20 20' aria-hidden='true'>
              <use href={`${sprite}#angle-down`}></use>
            </svg>
          </button>
          <button
            className='s-testimonials__navigate-right'
            title={__('testimonial-navigate-right')}
            aria-label={__('testimonial-navigate-right')}
            onClick={navigateRight}
          >
            <svg viewBox='0 0 20 20' aria-hidden='true'>
              <use href={`${sprite}#angle-down`}></use>
            </svg>
          </button>
        </div>
      )}
    </>

  );
};

Testimonials.propTypes = {
  entries: PropTypes.array
};

export default Testimonials;
